import React, { useState, useMemo } from 'react'
import { CategoryService } from '../services/CategoryService'

const FilterChips = ({ 
  episodes = [], 
  onFiltersChange, 
  selectedMoods = [], 
  selectedContentTypes = [] 
}) => {
  const [activeTab, setActiveTab] = useState('moods')

  // Calculer les statistiques pour chaque catégorie
  const categoryStats = useMemo(() => {
    if (!episodes || episodes.length === 0) return { moods: {}, contentTypes: {} }

    const moodCounts = {}
    const contentTypeCounts = {}

    episodes.forEach(episode => {
      // Compter les moods
      const episodeMoods = CategoryService.categorizeByMood(episode)
      episodeMoods.forEach(mood => {
        moodCounts[mood.key] = (moodCounts[mood.key] || 0) + 1
      })

      // Compter les types de contenu
      const episodeTypes = CategoryService.categorizeByContentType(episode)
      episodeTypes.forEach(type => {
        contentTypeCounts[type.key] = (contentTypeCounts[type.key] || 0) + 1
      })
    })

    return {
      moods: moodCounts,
      contentTypes: contentTypeCounts
    }
  }, [episodes])

  const categories = CategoryService.getAllCategories()

  const handleMoodToggle = (moodKey) => {
    const newSelectedMoods = selectedMoods.includes(moodKey)
      ? selectedMoods.filter(m => m !== moodKey)
      : [...selectedMoods, moodKey]
    
    onFiltersChange({
      moods: newSelectedMoods,
      contentTypes: selectedContentTypes
    })
  }

  const handleContentTypeToggle = (typeKey) => {
    const newSelectedTypes = selectedContentTypes.includes(typeKey)
      ? selectedContentTypes.filter(t => t !== typeKey)
      : [...selectedContentTypes, typeKey]
    
    onFiltersChange({
      moods: selectedMoods,
      contentTypes: newSelectedTypes
    })
  }

  const clearAllFilters = () => {
    onFiltersChange({
      moods: [],
      contentTypes: []
    })
  }

  const hasActiveFilters = selectedMoods.length > 0 || selectedContentTypes.length > 0

  return (
    <div className="filter-chips-container">
      {/* Tabs pour basculer entre moods et types */}
      <div className="flex justify-center mb-2 mt-2 bg-background-card rounded-lg p-2 gap-1">
        <button
          onClick={() => setActiveTab('moods')}
          className={`btn flex-1 py-3 px-6 ${
            activeTab === 'moods'
              ? 'btn-primary'
              : 'btn-secondary'
          }`}
        >
          Par mood
        </button>
        <button
          onClick={() => setActiveTab('contentTypes')}
          className={`btn flex-1 py-3 px-6 ${
            activeTab === 'contentTypes'
              ? 'btn-primary'
              : 'btn-secondary'
          }`}
        >
          Par type
        </button>
      </div>

      {/* Bouton clear filters */}
      {hasActiveFilters && (
        <div className="flex justify-center mb-6">
          <button
            onClick={clearAllFilters}
            className="btn btn-ghost py-2 px-4"
          >
            Effacer tous les filtres
          </button>
        </div>
      )}

      {/* Chips pour moods */}
      {activeTab === 'moods' && (
        <div className="flex flex-wrap justify-center gap-4 mb-6">
          {Object.entries(categories.moods).map(([key, mood]) => {
            const count = categoryStats.moods[key] || 0
            const isSelected = selectedMoods.includes(key)
            
            if (count === 0) return null
            
            return (
              <button
                key={key}
                onClick={() => handleMoodToggle(key)}
                className={`filter-chip ${isSelected ? 'filter-chip-active' : 'filter-chip-inactive'} py-3 px-4 rounded-3xl text-sm font-medium flex items-center justify-center gap-2 border-0 cursor-pointer min-h-10`}
              >
                <span>{mood.icon}</span>
                <span>{mood.name}</span>
                <span className="text-xs opacity-75">({count})</span>
              </button>
            )
          })}
        </div>
      )}

      {/* Chips pour types de contenu */}
      {activeTab === 'contentTypes' && (
        <div className="flex flex-wrap justify-center gap-4 mb-6">
          {Object.entries(categories.contentTypes).map(([key, type]) => {
            const count = categoryStats.contentTypes[key] || 0
            const isSelected = selectedContentTypes.includes(key)
            
            if (count === 0) return null
            
            return (
              <button
                key={key}
                onClick={() => handleContentTypeToggle(key)}
                className={`filter-chip ${isSelected ? 'filter-chip-active' : 'filter-chip-inactive'} py-3 px-4 rounded-3xl text-sm font-medium flex items-center justify-center gap-2 border-0 cursor-pointer min-h-10`}
              >
                <span>{type.icon}</span>
                <span>{type.name}</span>
                <span className="text-xs opacity-75">({count})</span>
              </button>
            )
          })}
        </div>
      )}

      {/* Message si aucun filtre disponible */}
      {activeTab === 'moods' && Object.keys(categoryStats.moods).length === 0 && (
        <div className="text-center text-text-muted py-4">
          <p className="text-sm">Aucune catégorie disponible pour ces épisodes</p>
        </div>
      )}

      {activeTab === 'contentTypes' && Object.keys(categoryStats.contentTypes).length === 0 && (
        <div className="text-center text-text-muted py-4">
          <p className="text-sm">Aucun type de contenu disponible pour ces épisodes</p>
        </div>
      )}
    </div>
  )
}

export default React.memo(FilterChips)